import React, { useEffect, useRef, useState } from 'react';
import { Button as RetryButton, Container } from 'reactstrap';
import { Form } from 'reactstrap';
import { Button } from '../../components/button/button';
import { FormProvider, useForm } from 'react-hook-form';
import { FiUser as UserIcon } from 'react-icons/fi';
import { AiFillLock as LockIcon } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoginMutation } from '../../redux/apiService/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import yupSchemaService from '../../services/yupSchemaService';
import { TextInput } from '../../components/form/textInput';
import toast from 'react-hot-toast';
import { routesPath } from '../../constants/routesPath';
import storageService from '../../services/storageService';
import { AdvancedLink } from '../../components/advancedLink/advancedLink';
import { siteLinks } from '../../constants/siteLinks';
import styles from '../loginContainer/loginContainer.module.scss';
import {
  loadAuthConfig,
  signedOutJustNow,
  signIn as keycloakSignIn,
} from '../../services/keycloakService';

const LegacyLoginContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const locale = queryParams.get('locale');
  const isStaging = process.env.REACT_APP_ENV === 'staging';

  const texts = locale === 'de' ? {
    title: 'Lass uns loslegen... Anmelden',
    email: 'E-Mail',
    password: 'Passwort',
    loginButton: 'Anmelden',
    success: 'Erfolgreich angemeldet!',
    error: 'E-Mail oder Passwort falsch!',
    linkText: 'Geschäftsbedingungen'
  } : {
    title: 'Let’s get hiring... Log in',
    email: 'Email',
    password: 'Password',
    loginButton: 'Log in',
    success: 'Successfully Logged In!',
    error: 'Email or Password Incorrect!',
    linkText: 'Terms and Conditions'
  };

  const onSubmit = (data) => {
    login(data);
  };
  const form = useForm({
    resolver: yupResolver(yupSchemaService.loginValidationSchema()),
    mode: 'all',
    defaultValues: {},
  });
  const [login, loginData] = useLoginMutation({
    fixedCacheKey: 'authData-sharing',
  });

  useEffect(() => {
    if (loginData.isSuccess) {
      storageService.setToken(
        loginData.data.data.token,
        loginData.data.data.user.email,
        loginData.data.data.user.id,
      );

      storageService.setUserInfo(loginData.data.data.user);
      toast.success(texts.success);
      navigate(routesPath.dashboard, { replace: false });
    } else if (loginData.isError) {
      const errorData = loginData.error?.data;
      const serverMsg = (Array.isArray(errorData?.errors) ? errorData.errors[0] : errorData?.errors) || errorData?.messages;
      toast.error(serverMsg || texts.error);
    }
  }, [loginData, navigate, texts]);

  return (
    <Container>
      <FormProvider {...form}>
        <Form onSubmit={form.handleSubmit(onSubmit)} className={styles.loginForm}>
          <div className={styles.loginFormInner}>
            <h2>
              {isStaging
                ? `[*STAGING*] ${texts.title} [*STAGING*]`
                : texts.title}
            </h2>
            <>
            <div className="mb-4">
              <TextInput
                icon={<UserIcon />}
                placeholder={texts.email}
                required={true}
                name="email"
                type="text"
              />
            </div>
            <div className="mb-4">
              <TextInput
                icon={<LockIcon />}
                placeholder={texts.password}
                required={true}
                name="password"
                type="password"
              />
            </div>

            <div className="col-12">
              <Button
                className="w-100"
                color="primary"
                type="submit"
                disabled={loginData.isLoading}
                submit
              >
                {texts.loginButton}
              </Button>
            </div>
            </>
            <center>
              <AdvancedLink
                href={siteLinks.termsAndConditions}
                target="_blank"
                className={styles.terms}
              >
                {texts.linkText}
              </AdvancedLink>
            </center>
          </div>
        </Form>
      </FormProvider>
    </Container>
  );
};

/**
 * HOLM-506: one shared login. When the backend runs Holedo sign-in (Keycloak),
 * this page shows no form of its own: it goes straight to the shared Holedo
 * page and comes back through /auth/callback. Only when the backend says
 * "legacy" (migration rollback) does the old email and password form appear.
 * If the sign-in settings cannot be loaded, it says so and offers a retry;
 * it never falls back to the password form.
 */
export const LoginContainer = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const locale = params.get('locale');
  const [legacy, setLegacy] = useState(false);
  const [error, setError] = useState('');
  const [signedOut, setSignedOut] = useState(false);
  const started = useRef(false);

  const texts =
    locale === 'de'
      ? {
          opening: 'Holedo-Anmeldung wird geöffnet…',
          failed: 'Die Anmeldung konnte nicht gestartet werden',
          retry: 'Erneut versuchen',
          signedOut: 'Sie haben sich abgemeldet.',
          signIn: 'Anmelden',
          fallback: 'Die Anmeldung ist momentan nicht verfügbar. Bitte versuchen Sie es erneut.',
        }
      : {
          opening: 'Opening Holedo sign-in…',
          failed: 'Sign-in could not be started',
          retry: 'Try again',
          signedOut: 'You have signed out.',
          signIn: 'Log in',
          fallback: 'Sign-in is unavailable right now. Please try again.',
        };

  // asked: the person pressed a button; otherwise this is the page opening.
  const start = async (asked = false) => {
    setError('');
    try {
      const config = await loadAuthConfig();
      if (!config.enabled) {
        setLegacy(true);
        return;
      }
      if (!asked && signedOutJustNow()) {
        // Right after signing out: stay here until the person asks.
        setSignedOut(true);
        return;
      }
      setSignedOut(false);
      await keycloakSignIn({
        returnPath: params.get('redirect') || '/',
        locale,
        replaceHistory: true,
      });
    } catch (e) {
      setError(e?.message || texts.fallback);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start(false);
    // Once per visit of this page; after a failure only the button starts it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (legacy) return <LegacyLoginContainer />;

  return (
    <Container className="py-5 text-center" aria-live="polite">
      {error ? (
        <>
          <h2>{texts.failed}</h2>
          <p>{error}</p>
          <RetryButton color="primary" onClick={() => start(true)}>
            {texts.retry}
          </RetryButton>
        </>
      ) : signedOut ? (
        <>
          <h2>{texts.signedOut}</h2>
          <RetryButton color="primary" onClick={() => start(true)}>
            {texts.signIn}
          </RetryButton>
        </>
      ) : (
        <h2>{texts.opening}</h2>
      )}
    </Container>
  );
};
